"use client";

import { useEffect } from "react";
import {
  clearKeyboardScrollPadding,
  isKeyboardOpen,
  isKeyboardScrollField,
  keyboardOverlapPx,
  scheduleKeyboardScrollIntoView,
  shouldRescrollOnViewportResize,
  writeKeyboardInsetVar,
} from "@/lib/virtualspace/keyboard-scroll-focused-field";

/**
 * App-wide: when a text / date / time / reason field is focused,
 * keep it visible above the mobile keyboard and bottom hub.
 * Mounted once from the root layout — SpaceManager, Spatialist,
 * and SpaceStation share the same schedule form.
 *
 * The field is pulled into view on focus and when the keyboard opens or
 * changes height. It is never pulled back while the user is scrolling:
 * `visualViewport.scroll` only refreshes the inset var, and a wheel or
 * touchmove cancels any pending re-scroll until the next focus or
 * keyboard transition. Otherwise scrolling past a field you just
 * typed in fights the helper frame by frame.
 */
export default function KeyboardScrollFocusedField() {
  useEffect(() => {
    let cancelScheduled: (() => void) | null = null;
    let lastVisualHeight: number | null = null;

    const currentVisualHeight = () =>
      Math.round(window.visualViewport?.height ?? window.innerHeight);

    const syncInset = () => {
      writeKeyboardInsetVar(keyboardOverlapPx());
    };

    const schedule = (el: EventTarget | null) => {
      cancelScheduled?.();
      cancelScheduled = null;
      if (!isKeyboardScrollField(el)) return;
      cancelScheduled = scheduleKeyboardScrollIntoView(el);
    };

    const onFocusIn = (event: FocusEvent) => {
      lastVisualHeight = currentVisualHeight();
      schedule(event.target);
    };

    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (isKeyboardScrollField(next)) return;
      window.setTimeout(() => {
        if (!isKeyboardScrollField(document.activeElement)) {
          clearKeyboardScrollPadding();
          writeKeyboardInsetVar(keyboardOverlapPx());
        }
      }, 0);
    };

    const onUserScroll = () => {
      cancelScheduled?.();
      cancelScheduled = null;
    };

    const onViewportScroll = () => {
      syncInset();
    };

    const onViewportResize = () => {
      syncInset();
      const focused = document.activeElement;
      const nextHeight = currentVisualHeight();
      const previousHeight = lastVisualHeight;
      lastVisualHeight = nextHeight;
      if (!isKeyboardScrollField(focused)) return;

      const overlap = keyboardOverlapPx();
      if (!isKeyboardOpen(overlap)) {
        // Keyboard dismissed with the field still focused (iOS Done).
        // Drop the extra scroll range; do not move the page.
        clearKeyboardScrollPadding();
        return;
      }
      if (
        !shouldRescrollOnViewportResize({ previousHeight, nextHeight, overlapPx: overlap })
      ) {
        return;
      }
      // A real keyboard transition re-arms the helper even after the user
      // scrolled: the field they are typing into may now be covered.
      schedule(focused);
    };

    syncInset();
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("wheel", onUserScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("touchmove", onUserScroll, {
      passive: true,
      capture: true,
    });
    const vv = window.visualViewport;
    vv?.addEventListener("resize", onViewportResize);
    vv?.addEventListener("scroll", onViewportScroll);
    window.addEventListener("resize", onViewportResize);

    return () => {
      cancelScheduled?.();
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("wheel", onUserScroll, true);
      window.removeEventListener("touchmove", onUserScroll, true);
      vv?.removeEventListener("resize", onViewportResize);
      vv?.removeEventListener("scroll", onViewportScroll);
      window.removeEventListener("resize", onViewportResize);
      clearKeyboardScrollPadding();
      writeKeyboardInsetVar(0);
    };
  }, []);

  return null;
}
